/**
 * Winners / losers grid (Phase-3 scaffold). At RESULT/RESOLUTION it shows the
 * ranking with the winner and worst-loser highlighted and per-player payout
 * deltas (rolling counter). Phase 3 adds the close-up blob win/lose poses; here
 * we lay out the data-bound grid from `resolution` + `result`.
 */

import { useGameStore } from '../state/store';
import { formatCents } from '../util/format';

export function WinnersLosersGrid() {
  const phase = useGameStore((s) => s.server.phase);
  const resolution = useGameStore((s) => s.server.resolution);
  const result = useGameStore((s) => s.server.result);
  const roster = useGameStore((s) => s.server.roster);

  if (phase !== 'RESULT' && phase !== 'RESOLUTION') return null;
  if (!resolution) return null;

  const nameFor = (id: string) =>
    id === '__house__' ? 'House' : (roster.find((r) => r.id === id)?.name ?? id.slice(0, 6));
  const payoutFor = (id: string) => result?.payouts.find((p) => p.id === id)?.deltaCents;

  const winnerId = result?.winnerId ?? resolution.winnerId;
  const loserId = result?.worstLoserId ?? resolution.worstLoserId;

  return (
    <div className="panel results">
      <h3 className="panel__title heading-gold">Results</h3>
      <ul className="results__list">
        {resolution.ranking.map((r) => {
          const delta = payoutFor(r.id);
          const isWinner = r.id === winnerId;
          const isLoser = r.id === loserId;
          return (
            <li
              key={r.id}
              className={`results__row ${isWinner ? 'is-winner' : ''} ${isLoser ? 'is-loser' : ''}`}
            >
              <span className="results__rank numeric">#{r.rank}</span>
              <span className="results__name">{nameFor(r.id)}</span>
              <span className="results__total numeric">
                {r.isBust ? `BUST ${r.total}` : r.total}
                {r.isNatural ? ' ♠' : ''}
              </span>
              {delta !== undefined && (
                <span className={`results__delta numeric ${delta >= 0 ? 'pos' : 'neg'}`}>
                  {delta >= 0 ? '+' : ''}
                  {formatCents(delta)}
                </span>
              )}
              {isWinner && <span className="results__tag tag-win">Winner</span>}
              {isLoser && <span className="results__tag tag-lose">Worst</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
